import React, { useState } from 'react';
import * as Yup from 'yup';
import { JourneyForm, JourneyFormik } from 'src/common/JourneyFormik';
import { apiRequestAccessToJourney } from 'src/utils/journeyApi';
import CustomBrandedButton from 'src/common/CustomBrandedButton';
import { FormikTextInput } from 'src/common/form/formik/text-input';
import { FormikTextarea } from 'src/common/form/formik/textarea';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { useGlobals } from 'src/store/globals';
import classNames from 'classnames';

const LEARN_ABOUT_JOURNEY_URL = 'https://journey.io';

type JourneyRequestAccessContentProps = {
  journey: any;
  initialEmail?: string | null;
};

export function JourneyRequestAccessContent({ journey, initialEmail = '' }: JourneyRequestAccessContentProps) {
  const [requested, setRequested] = useState(false);
  const { setErrorNotification } = useNotificationMessage();
  const isBedrock = useGlobals((state) => state.isBedrock);

  const containerClass = classNames('flex flex-col', {
    'space-y-4 text-center': !isBedrock,
    'space-y-4 text-bedrock-black': isBedrock,
  });

  const headerClasses = classNames({
    'text-bedrock-h2': isBedrock,
    'text-gray-900 text-xl': !isBedrock,
  });

  const copyClasses = classNames('mt-2!', {
    'text-bedrock-p': isBedrock,
    'text-secondary': !isBedrock,
  });

  const subtitleClasses = classNames('text-left !mt-2', {
    'text-xs text-gray-500': !isBedrock,
    'text-bedrock-p-small text-bedrock-dark-gray': isBedrock,
  });

  const formikParams = {
    initialValues: {
      email: initialEmail || '',
      comment: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values: any) => {
      apiRequestAccessToJourney(journey.alias.uuid, { email: values.email, comment: values.comment || '' })
        .then(() => {
          setRequested(true);
        })
        .catch((e) => {
          setErrorNotification('Something went wrong. Please try again');
        });
    },
    validationSchema: Yup.object({
      ...{ email: Yup.string().email('Invalid email address').required('Required') },
    }),
  };

  const renderRequestSentDialog = () => {
    return (
      <div className={containerClass}>
        <div className={headerClasses}>Request sent</div>
        <div className={copyClasses}>
          Your request has been sent to <span className='font-medium'>{journey.organization.name}</span>, you'll receive
          an email upon access being granted.
        </div>
        {isBedrock ? (
          <div className='flex justify-start'>
            <button
              type='button'
              className='btn-bedrock-primary'
              onClick={() => {
                window.location.href = LEARN_ABOUT_JOURNEY_URL;
              }}
            >
              Learn about Journey
            </button>
          </div>
        ) : (
          <CustomBrandedButton
            className='w-full justify-center items-center !mt-6 text-center'
            journey={journey}
            onClick={() => {
              window.location.href = LEARN_ABOUT_JOURNEY_URL;
            }}
          >
            Learn about Journey
          </CustomBrandedButton>
        )}
      </div>
    );
  };

  const renderRequestAccessDialog = () => {
    return (
      <div className={containerClass}>
        <div className={headerClasses}>Request access</div>
        <div className={copyClasses}>You do not have access to this Journey but can request access</div>
        <JourneyFormik {...formikParams}>
          <JourneyForm className='relative flex flex-col space-y-2 text-left'>
            <FormikTextInput
              id='gate-email'
              name='email'
              type='text'
              required={true}
              placeholder='Email Address'
              inputClasses={'h-12'}
              autoFocus={true}
            />
            <FormikTextarea inputClasses='h-24' id='comment' name='comment' hideOptional={true} placeholder='Comment' />
            {isBedrock ? (
              <div className='flex justify-start'>
                <button type='submit' className='btn-bedrock-primary'>
                  Request Access
                </button>
              </div>
            ) : (
              <CustomBrandedButton
                className={'w-full justify-center items-center !mt-6'}
                journey={journey}
                type='submit'
              >
                Request Access
              </CustomBrandedButton>
            )}
            <div className={subtitleClasses}>Your email will only be shared with {journey.organization.name}</div>
          </JourneyForm>
        </JourneyFormik>
      </div>
    );
  };

  return requested ? renderRequestSentDialog() : renderRequestAccessDialog();
}
